import {
  AutomationComparison,
  AutomationConditionType,
} from '@/typings/api/emailCenter';
import { getIntlTranslation } from '@/utils/intl';
import { MinusCircleOutlined } from '@ant-design/icons';
import { Button, Input, Select } from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import { FormCondition, VALUELESS_COMPARISONS } from './sections';

import styles from './AutomationsTab.less';

const CONDITION_TYPES: AutomationConditionType[] = [
  'attribute',
  'has_tag',
  'has_not_tag',
];
const COMPARISONS: AutomationComparison[] = [
  '=',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'in',
  'not_in',
  'is_empty',
  'is_not_empty',
];

interface ConditionRowProps {
  value: FormCondition;
  onChange: (next: FormCondition) => void;
  onRemove: () => void;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  value,
  onChange,
  onRemove,
}) => {
  const intl = useIntl();
  const isAttribute = value.type === 'attribute';
  const needsValue =
    !isAttribute || !VALUELESS_COMPARISONS.includes(value.comparison as any);

  return (
    <div className={styles.conditionRow}>
      <Select
        size="small"
        className={styles.conditionType}
        value={value.type}
        onChange={(type) =>
          onChange({
            type,
            ...(type === 'attribute' ? { comparison: '=' } : {}),
          })
        }
        options={CONDITION_TYPES.map((type) => ({
          value: type,
          label: getIntlTranslation(intl, {
            id: `pages.emailCenter.automations.condition.type.${type}`,
            defaultMessage: type,
          }),
        }))}
      />

      {isAttribute && (
        <>
          <Input
            size="small"
            className={styles.conditionAttribute}
            value={value.attribute}
            onChange={(event) =>
              onChange({ ...value, attribute: event.target.value })
            }
            placeholder={getIntlTranslation(intl, {
              id: 'pages.emailCenter.automations.condition.attribute',
              defaultMessage: 'Attribute',
            })}
          />
          <Select
            size="small"
            className={styles.conditionComparison}
            value={value.comparison}
            onChange={(comparison) => onChange({ ...value, comparison })}
            options={COMPARISONS.map((comparison) => ({
              value: comparison,
              label: getIntlTranslation(intl, {
                id: `pages.emailCenter.automations.condition.comparison.${comparison}`,
                defaultMessage: comparison,
              }),
            }))}
          />
        </>
      )}

      {needsValue && (
        <Input
          size="small"
          className={styles.conditionValue}
          value={value.value}
          onChange={(event) =>
            onChange({ ...value, value: event.target.value })
          }
          placeholder={getIntlTranslation(intl, {
            id: isAttribute
              ? 'pages.emailCenter.automations.condition.value'
              : 'pages.emailCenter.automations.condition.tag',
            defaultMessage: isAttribute ? 'Value' : 'Tag',
          })}
        />
      )}

      <Button
        type="text"
        size="small"
        icon={<MinusCircleOutlined />}
        onClick={onRemove}
      />
    </div>
  );
};

export default React.memo(ConditionRow);
